import React, { FC } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { SessionArtifactNoteRenderer } from "../session-artifact-note-renderer";
import { useMDSSession } from "../../../session-context";
import { MDSSessionArtifact } from "@/mds/components/sessions/session-context";
import {
  ArtifactV1SessionPartKind,
  NoteCreatedV1ArtifactPayload,
  NoteUpdatedV1ArtifactPayload,
} from "@/mds/components/sessions/session.types";
import { DateTime } from "luxon";
import { MDSNotePreviewContainerFooter } from "@/mds/components/note-preview-container/footer";
import { MDSButton, MDSButtonSlotName, MDSButtonVariant } from "@/mds/components/buttons/button";
import { MDSIconButton } from "@/mds/components/buttons";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";

interface Props {
  isVisible: boolean;
}

export const SessionArtifactViewer: FC<Props> = ({ isVisible }) => {
  const { activeArtifact, closeArtifact } = useMDSSession();

  const noteUrl = getNoteUrl(activeArtifact);
  const handleOpenArtifact = (e: React.MouseEvent<Element>) => {
    if (noteUrl) {
      e.preventDefault();
      e.stopPropagation();

      // TODO: figure out how to deal with this: MDS component must not depend on large modules (windowModule) but desktop may fail here
      // We prob can refactor windowModule.openInNewTab into smaller utility and use it here
      // eslint-disable-next-line
      window.open(noteUrl, "_blank", "noopener noreferrer");
    }
  };

  const getNoteMetadata = () => {
    if (!activeArtifact) return null;

    if (activeArtifact.kind === ArtifactV1SessionPartKind.NoteCreatedV1) {
      const payload = activeArtifact.payload as NoteCreatedV1ArtifactPayload;
      return {
        primaryLabel: payload.primary_label,
        secondaryLabel: payload.secondary_label,
        modifiedAt: payload.note_updated_at,
        createdAt: payload.note_created_at,
      };
    } else if (activeArtifact.kind === ArtifactV1SessionPartKind.NoteUpdatedV1) {
      const payload = activeArtifact.payload as NoteUpdatedV1ArtifactPayload;
      return {
        primaryLabel: payload.primary_label,
        secondaryLabel: payload.secondary_label,
        modifiedAt: payload.note_updated_at,
        createdAt: payload.note_created_at,
      };
    }

    return null;
  };

  const metadata = getNoteMetadata();

  return (
    <AnimatePresence>
      {isVisible && activeArtifact && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          exit={{ opacity: 0, transition: { duration: 0.1 } }}
        >
          <Container>
            <Header>
              <MDSButton variant={MDSButtonVariant.Transparent} onClick={closeArtifact}>
                <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.LeftArrow} />
                &nbsp;Back
              </MDSButton>
              {noteUrl && (
                <MDSIconButton iconKind={MDSIconKind.ExternalLink} onClick={handleOpenArtifact} />
              )}
            </Header>
            <ContentWrapper>
              <Content>
                <ArtifactRenderer artifact={activeArtifact} />
              </Content>
              {metadata && (
                <MDSNotePreviewContainerFooter
                  title={metadata.primaryLabel}
                  createdAt={metadata.createdAt}
                  modifiedAt={metadata.modifiedAt}
                  updateOnly={false}
                  authorNames={[]}
                  secondaryLabel={metadata.secondaryLabel}
                />
              )}
            </ContentWrapper>
          </Container>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

const ArtifactRenderer = ({ artifact }: { artifact: MDSSessionArtifact }) => {
  switch (artifact.kind) {
    case ArtifactV1SessionPartKind.NoteCreatedV1: {
      const payload = artifact.payload as NoteCreatedV1ArtifactPayload;

      return (
        <SessionArtifactNoteRenderer
          data={{
            noteInfo: {
              noteId: payload.note_id,
              createdAt: DateTime.fromISO(payload.note_created_at),
              updatedAt: DateTime.fromISO(payload.note_updated_at),
              encodedContent: payload.encoded_content,
              primaryLabel: payload.primary_label,
              secondaryLabel: payload.secondary_label,
              markdownContent: payload.markdown_content,
            },
          }}
        />
      );
    }
    case ArtifactV1SessionPartKind.NoteUpdatedV1: {
      const payload = artifact.payload as NoteUpdatedV1ArtifactPayload;

      return (
        <SessionArtifactNoteRenderer
          data={{
            noteInfo: {
              noteId: payload.note_id,
              createdAt: DateTime.fromISO(payload.note_created_at),
              updatedAt: DateTime.fromISO(payload.note_updated_at),
              encodedContent: payload.encoded_content,
              primaryLabel: payload.primary_label,
              secondaryLabel: payload.secondary_label,
              markdownContent: payload.markdown_content,
            },
            previousNoteInfo: {
              noteId: payload.note_id,
              createdAt: DateTime.fromISO(payload.note_created_at),
              updatedAt: DateTime.fromISO(payload.note_updated_at),
              encodedContent: payload.prev_encoded_content,
              primaryLabel: payload.prev_primary_label,
              secondaryLabel: payload.prev_secondary_label,
              markdownContent: payload.prev_markdown_content,
            },
            showDifferences: true,
          }}
        />
      );
    }
  }
};

const getNoteUrl = (activeArtifact: MDSSessionArtifact | null) => {
  if (!activeArtifact) return;
  if (
    activeArtifact.kind === ArtifactV1SessionPartKind.NoteCreatedV1 ||
    activeArtifact.kind === ArtifactV1SessionPartKind.NoteUpdatedV1
  ) {
    const noteId = (activeArtifact.payload as NoteCreatedV1ArtifactPayload).note_id;
    return `https://mem.ai/notes/${noteId}`;
  }
};

const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
  height: 100%;
  max-height: 100%;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.preview};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  width: 100%;
  max-width: 399px;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing["2"]};
  background-color: ${({ theme }) => theme.colors.surface.previewHeader};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

  // Special hack: for some reason MdsIcon has trouble rendering in chrome extensions
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  overflow: auto;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
